import { memo } from "react";
import { useTranslate } from "helpers";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonGroup from "ui/components/Button/ButtonMenu/ButtonGroup";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { VehicleDetailRef } from "../../../VehicleDetailCard/types";
import {
  VEHICLE_CONTRACT_WIDGET_ID,
  VEHICLE_INVOICE_WIDGET_ID,
  VEHICLE_OFFER_WIDGET_ID,
} from "../../../VehicleDetailCard/components/WidgetEditCard/consts";

interface VehicleDocumentationMenuProps {
  vehicleId: string;
  /** Prodano vozilo je arhivirano — na njemu se ne kreiraju ni ponude, ni ugovori, ni računi. */
  isSold?: boolean;
  vehicleDetailRef?: React.RefObject<VehicleDetailRef | null>;
  /** Stavka se nudi samo ako je njezin widget u rasporedu — bez njega nema što otvoriti. */
  userWidgets: string[];
}

const SOLD_TOOLTIP = "Vozilo je prodano";

const VehicleDocumentationMenu: React.FC<VehicleDocumentationMenuProps> = ({ vehicleId, isSold, vehicleDetailRef, userWidgets }) => {
  const { t } = useTranslate();

  const hasOffer = userWidgets.includes(VEHICLE_OFFER_WIDGET_ID);
  const hasContract = userWidgets.includes(VEHICLE_CONTRACT_WIDGET_ID);
  const hasInvoice = userWidgets.includes(VEHICLE_INVOICE_WIDGET_ID);

  const handleOfferClick = () => {
    vehicleDetailRef?.current?.openAddNewOfferSale();
  };

  const handleContractClick = () => {
    vehicleDetailRef?.current?.openAddNewContractSale();
  };

  const handleInvoiceClick = () => {
    vehicleDetailRef?.current?.openAddNewInvoiceSale();
  };

  const handleOfferRentClick = () => {
    vehicleDetailRef?.current?.openAddNewOfferRent();
  };

  const handleContractRentClick = () => {
    vehicleDetailRef?.current?.openAddNewContractRent();
  };

  const handleInvoiceRentClick = () => {
    vehicleDetailRef?.current?.openAddNewInvoiceRent();
  };

  if (!hasOffer && !hasContract && !hasInvoice) return null;

  return (
    <>
      <ButtonMenu
        text={t("admin.fleet.vehicle.detail.documentation.menu")}
        size="large"
        variant="secondary"
        endIcon="/media/icons/chevron-down.svg"
        groupDirection="horizontal"
      >
        <ButtonGroup text={t("admin.fleet.vehicle.detail.documentation.menu.group.sale")}></ButtonGroup>

        {hasOffer && (
          <ButtonMenuItem
            startIcon="/media/icons/offer.svg"
            onClick={handleOfferClick}
            disabled={isSold}
            tooltip={isSold ? SOLD_TOOLTIP : undefined}
          >
            {t("admin.fleet.vehicle.detail.documentation.menu.sale.offer")}
          </ButtonMenuItem>
        )}
        {hasContract && (
          <ButtonMenuItem
            startIcon="/media/icons/contract.svg"
            onClick={handleContractClick}
            disabled={isSold}
            tooltip={isSold ? SOLD_TOOLTIP : undefined}
          >
            {t("admin.fleet.vehicle.detail.documentation.menu.sale.contract")}
          </ButtonMenuItem>
        )}
        {hasInvoice && (
          <ButtonMenuItem
            startIcon="/media/icons/invoice.svg"
            onClick={handleInvoiceClick}
            disabled={isSold}
            tooltip={isSold ? SOLD_TOOLTIP : undefined}
          >
            {t("admin.fleet.vehicle.detail.documentation.menu.invoice")}
          </ButtonMenuItem>
        )}

        <ButtonGroup text="NAJAM VOZILA"></ButtonGroup>

        {hasOffer && (
          <ButtonMenuItem
            startIcon="/media/icons/offer.svg"
            onClick={handleOfferRentClick}
            disabled={isSold}
            tooltip={isSold ? SOLD_TOOLTIP : undefined}
          >
            Ponuda
          </ButtonMenuItem>
        )}
        {hasContract && (
          <ButtonMenuItem
            startIcon="/media/icons/contract.svg"
            onClick={handleContractRentClick}
            disabled={isSold}
            tooltip={isSold ? SOLD_TOOLTIP : undefined}
          >
            Ugovor
          </ButtonMenuItem>
        )}
        {hasInvoice && (
          <ButtonMenuItem
            startIcon="/media/icons/invoice.svg"
            onClick={handleInvoiceRentClick}
            disabled={isSold}
            tooltip={isSold ? SOLD_TOOLTIP : undefined}
          >
            Račun
          </ButtonMenuItem>
        )}
      </ButtonMenu>
    </>
  );
};

export default memo(VehicleDocumentationMenu);
